// campaign-editor-dialog.tsx — the campaign message editors' frame: the SAME
// EditorPreviewDialog the schedule-message editor opens in (editor card + the
// WhatsApp preview beside it), holding a redesign-styled card.
//
// Why not the old hand-rolled fixed overlay: at z-index 2147483000 it buried
// every Radix dialog the shared message editor opens from inside it (Templates,
// Save as template), so they opened behind the modal. In the shared Dialog they
// stack by Radix's layer order, exactly as they do in the schedule-message
// editor — one behaviour to maintain and test, not two.
import React from 'react';

import { usePortalContainer } from '@/common/hooks/use-portal-container';
import { REDESIGN_SCOPE_CLASS, resolveRedesignTheme } from '@common-components/modal-overlay/modal-overlay';
import EditorPreviewDialog from '../add-scheduled-message-button/editor-preview-dialog';

interface CampaignEditorDialogProps {
  onClose: () => void;
  /** Screen-reader title; the card renders its own visible header. */
  title: string;
  /** The live WhatsApp preview column. It reads the editor draft that
   *  DripMessageEditor mirrors, so show it only while that editor is mounted. */
  showPreview?: boolean;
  maxWidth?: number;
  children: React.ReactNode;
}

export const CampaignEditorDialog: React.FC<CampaignEditorDialogProps> = ({
  onClose,
  title,
  showPreview = true,
  maxWidth,
  children,
}) => {
  const container = usePortalContainer();
  return (
    <EditorPreviewDialog
      open
      onClose={onClose}
      title={title}
      showPreview={showPreview}
      showCloseButton={false}
      maxWidth={maxWidth}
    >
      {/* The Dialog portals out of the app's `.campaigns-rd` wrapper, where the
          redesign tokens (--surface, --line, .btn …) are declared — re-apply the
          scope and the active theme on the card, as ModalOverlay does. */}
      <div
        className={REDESIGN_SCOPE_CLASS}
        data-theme={resolveRedesignTheme(container)}
        style={{
          flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column',
          background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 16,
          overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,.28)',
        }}
      >
        {children}
      </div>
    </EditorPreviewDialog>
  );
};
